// Auth provider: dev mode (the local developer; sign-out lasts for the browser session) or password mode (session cookie checked with GET /me; the sign-in post answers the two-step sign-in step and the session starts only when the screen hands over the user after the code or the authenticator setup; sign-out posts, and a 401 unauthenticated from any call signs the user out); sign-in drops the previous user's cache and sign-out clears it (the Assistant conversations live on the server per user, so the browser keeps no copy to clear).
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";

import { authSignIn, authSignOut, type Me, type SignInStep } from "@/api/__generated__";
import { setSignedOutHandler } from "@/api/client";
import { isApiProblem } from "@/api/problem";
import { AuthContext, type AuthState, type AuthStatus, type Credentials } from "@/app/auth/useAuth";
import type { AuthConfig } from "@/app/config";
import { meQueryOptions } from "@/app/session";
import { readSession, SESSION_KEYS, writeSession } from "@/lib/storage";
import { shouldRetry } from "@/query-client";

type SignedOutCallback = () => Promise<unknown> | undefined;

interface Session {
  status: AuthStatus;
  signedOut: boolean;
  checkError: unknown;
}

interface ProviderProps {
  onSignedOut?: SignedOutCallback | undefined;
  children: ReactNode;
}

const SIGNED_IN: Session = { status: "authenticated", signedOut: false, checkError: null };
const EXPIRED: Session = { status: "unauthenticated", signedOut: false, checkError: null };
const SIGNED_OUT: Session = { status: "unauthenticated", signedOut: true, checkError: null };

function retryOnce(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && shouldRetry(failureCount, error);
}

function seedSession(queryClient: QueryClient, me: Me): void {
  queryClient.clear();
  queryClient.setQueryData(meQueryOptions().queryKey, me);
}

async function finishSignOut(queryClient: QueryClient, onSignedOut: SignedOutCallback | undefined) {
  await onSignedOut?.();
  queryClient.clear();
}

function DevAuthProvider({ onSignedOut, children }: ProviderProps) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session>(() =>
    readSession(SESSION_KEYS.devSignedOut) === "1" ? EXPIRED : SIGNED_IN,
  );
  const signIn = useCallback((): Promise<SignInStep | null> => {
    writeSession(SESSION_KEYS.devSignedOut, null);
    flushSync(() => {
      setSession(SIGNED_IN);
    });
    return Promise.resolve(null);
  }, []);
  const startSession = useCallback(
    (me: Me) => {
      writeSession(SESSION_KEYS.devSignedOut, null);
      seedSession(queryClient, me);
      flushSync(() => {
        setSession(SIGNED_IN);
      });
    },
    [queryClient],
  );
  const signOut = useCallback(async () => {
    writeSession(SESSION_KEYS.devSignedOut, "1");
    flushSync(() => {
      setSession(SIGNED_OUT);
    });
    await finishSignOut(queryClient, onSignedOut);
  }, [queryClient, onSignedOut]);
  const value = useMemo<AuthState>(
    () => ({ mode: "dev", ...session, signIn, startSession, signOut, recheck: () => undefined }),
    [session, signIn, startSession, signOut],
  );
  return <AuthContext value={value}>{children}</AuthContext>;
}

function PasswordAuthProvider({ onSignedOut, children }: ProviderProps) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session>({ status: "loading", signedOut: false, checkError: null });
  const [check, setCheck] = useState(0);

  useEffect(() => {
    let active = true;
    queryClient.query({ ...meQueryOptions(), retry: retryOnce }).then(
      () => {
        if (active) {
          setSession(SIGNED_IN);
        }
      },
      (error: unknown) => {
        if (active) {
          const signedOut = isApiProblem(error) && error.status === 401;
          setSession({ status: "unauthenticated", signedOut: false, checkError: signedOut ? null : error });
        }
      },
    );
    return () => {
      active = false;
    };
  }, [queryClient, check]);

  useEffect(
    () =>
      setSignedOutHandler(() => {
        setSession((current) => (current.status === "authenticated" ? EXPIRED : current));
      }),
    [],
  );

  const startSession = useCallback(
    (me: Me) => {
      seedSession(queryClient, me);
      flushSync(() => {
        setSession(SIGNED_IN);
      });
    },
    [queryClient],
  );
  const signIn = useCallback(async (credentials?: Credentials): Promise<SignInStep | null> => {
    if (credentials === undefined) {
      throw new Error("Enter your email address and password.");
    }
    const { data } = await authSignIn({ body: credentials, throwOnError: true });
    return data;
  }, []);
  const signOut = useCallback(async () => {
    await authSignOut({ throwOnError: true });
    flushSync(() => {
      setSession(SIGNED_OUT);
    });
    await finishSignOut(queryClient, onSignedOut);
  }, [queryClient, onSignedOut]);
  const recheck = useCallback(() => {
    setCheck((count) => count + 1);
  }, []);
  const value = useMemo<AuthState>(
    () => ({ mode: "password", ...session, signIn, startSession, signOut, recheck }),
    [session, signIn, startSession, signOut, recheck],
  );
  return <AuthContext value={value}>{children}</AuthContext>;
}

export function AuthProvider({ config, onSignedOut, children }: ProviderProps & { config: AuthConfig }) {
  if (config.mode === "password") {
    return <PasswordAuthProvider onSignedOut={onSignedOut}>{children}</PasswordAuthProvider>;
  }
  return <DevAuthProvider onSignedOut={onSignedOut}>{children}</DevAuthProvider>;
}
